/**
 * CommentSection — Full-featured comment panel for Procurement Requests.
 *
 * Features:
 * - Add public / internal comments with @mention support
 * - Inline edit (author only) with edit history indicator
 * - Soft delete (author or admin)
 * - @mention tags highlighted in blue, mention badges listed below comment
 * - INTERNAL comments: yellow border + badge (staff-only visible)
 * - Smart quick-comment suggestions derived from the request's live data
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Heading,
  Flex,
  HStack,
  VStack,
  Text,
  Textarea,
  Input,
  Select,
  Button,
  Badge,
  Avatar,
  Divider,
  Spinner,
  Wrap,
  WrapItem,
  useColorModeValue,
  useToast,
  Collapse,
  Icon,
  Link,
} from "@chakra-ui/react";
import {
  BsSend,
  BsPencilSquare,
  BsTrash,
  BsChatDots,
  BsChevronDown,
  BsChevronUp,
  BsLockFill,
  BsPaperclip,
  BsX,
} from "react-icons/bs";
import procurementApi from "../services/procurementApi";
import { useAuth } from "../../../context/AuthContext";
import { employeeService } from "../../employee/employeeService";
import WysiwygEditor from "../../../components/WysiwygEditor";

/* ── Roles that can post / view INTERNAL comments ────────────────────────── */
const INTERNAL_ROLES = [
  "Super Admin",
  "Admin",
  "HR Manager",
  "HR",
  "Manager",
  "Finance Manager",
];

const ADMIN_ROLES = ["Super Admin", "Admin", "HR Manager"];

/* ── Build smart quick-comment suggestions from the request object ────────── */
function buildSuggestions(request) {
  if (!request) return [];

  const suggestions = [];

  // Status-aware suggestions
  if (request.status === "DRAFT") {
    suggestions.push("This request is ready for submission.");
    suggestions.push("Please review the line items before submitting.");
  }
  if (request.status === "SUBMITTED" || request.status === "IN_REVIEW") {
    suggestions.push(
      `Reviewing request for ${request.department || "the department"}.`,
    );
    suggestions.push("Additional documents may be required before approval.");
    if (request.currentApprover?.name)
      suggestions.push(
        `Awaiting approval from ${request.currentApprover.name}.`,
      );
  }
  if (request.status === "APPROVED") {
    suggestions.push("This request has been approved. Please proceed.");
    suggestions.push("Kindly initiate the vendor selection process.");
  }
  if (request.status === "REJECTED") {
    suggestions.push(
      "Please revise and resubmit after addressing the concerns.",
    );
  }

  // Priority suggestions
  if (request.priority === "URGENT") {
    suggestions.push("This is an URGENT request — please expedite.");
  }
  if (request.priority === "HIGH") {
    suggestions.push("High-priority request — please process promptly.");
  }

  // Budget suggestion
  if (request.estimatedBudget) {
    suggestions.push(
      `Estimated budget of ₹${request.estimatedBudget.toLocaleString()} — please confirm Finance approval.`,
    );
  }

  // Line items suggestion
  if (request.lineItems?.length) {
    suggestions.push(
      `Please review all ${request.lineItems.length} line item(s) for accuracy.`,
    );
  }

  // Delivery date suggestion
  if (request.expectedDeliveryDate) {
    const date = new Date(request.expectedDeliveryDate).toLocaleDateString();
    suggestions.push(`Expected delivery by ${date} — please plan accordingly.`);
  }

  return suggestions.slice(0, 5);
}

/* ── Highlight @mentions inline ──────────────────────────────────────────── */
function RenderContent({ content }) {
  const parts = content.split(/(@[\w.@+-]+)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("@") ? (
          <Text
            as="span"
            key={i}
            color="blue.500"
            fontWeight="semibold"
            bg="blue.50"
            px="2px"
            borderRadius="sm"
          >
            {part}
          </Text>
        ) : (
          <Text as="span" key={i}>
            {part}
          </Text>
        ),
      )}
    </>
  );
}

function formatRelativeTime(value) {
  if (!value) return "";
  const now = Date.now();
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";

  const diffMs = now - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(value).toLocaleDateString();
}

function getDateGroupLabel(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Earlier";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffDays = Math.floor((today - target) / (24 * 60 * 60 * 1000));
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return "Earlier";
}

function getPlainTextFromHtml(html) {
  const temp = document.createElement("div");
  temp.innerHTML = html || "";
  return (temp.textContent || temp.innerText || "").trim();
}

/* ── Single comment card ─────────────────────────────────────────────────── */
function CommentCard({
  comment,
  currentUser,
  onEdit,
  onDelete,
  editingId,
  editingText,
  setEditingText,
  onSaveEdit,
  onCancelEdit,
  initialExpanded = false,
  isHighlighted = false,
}) {
  const isInternal = comment.visibility === "INTERNAL";
  const isAuthor =
    currentUser?._id === (comment.authorId?._id || comment.authorId);
  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);

  const cardBg = useColorModeValue(
    isInternal ? "yellow.50" : "white",
    isInternal ? "yellow.900" : "gray.800",
  );
  const borderColor = useColorModeValue(
    isInternal ? "yellow.300" : "gray.200",
    isInternal ? "yellow.600" : "gray.700",
  );
  const metaColor = useColorModeValue("gray.500", "gray.400");
  const flashBg = useColorModeValue("blue.50", "blue.900");

  const isEditing = editingId === comment._id;
  const [expanded, setExpanded] = useState(initialExpanded);
  const isLong = (comment.content || "").length > 280;
  const displayContent =
    isLong && !expanded
      ? `${comment.content.slice(0, 280)}...`
      : comment.content;

  return (
    <Box
      bg={isHighlighted ? flashBg : cardBg}
      borderRadius="lg"
      p={4}
      border="1px solid"
      borderColor={isHighlighted ? "blue.300" : borderColor}
      position="relative"
      transition="background-color 0.25s ease, border-color 0.25s ease"
    >
      {/* Header row */}
      <Flex justify="space-between" align="flex-start">
        <HStack spacing={3} align="flex-start">
          <Avatar size="xs" name={comment.authorName} />
          <Box>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontWeight="bold" fontSize="sm">
                {comment.authorName}
                {isAuthor && (
                  <Text as="span" ml={1} fontWeight="normal" color={metaColor}>
                    (you)
                  </Text>
                )}
              </Text>
              {isInternal && (
                <Badge
                  colorScheme="yellow"
                  variant="solid"
                  fontSize="10px"
                  display="flex"
                  alignItems="center"
                  borderRadius="md"
                >
                  <Icon as={BsLockFill} boxSize="9px" mr={1} />
                  Internal
                </Badge>
              )}
              {!isInternal && (
                <Badge
                  colorScheme="blue"
                  variant="subtle"
                  fontSize="10px"
                  display="flex"
                  alignItems="center"
                  borderRadius="md"
                >
                  <Icon as={BsChatDots} boxSize="9px" mr={1} />
                  Public
                </Badge>
              )}
            </HStack>
            <Text fontSize="xs" color={metaColor}>
              {formatRelativeTime(comment.createdAt)}
              <Text as="span" ml={1}>
                ({new Date(comment.createdAt).toLocaleString()})
              </Text>
              {comment.isEdited && (
                <Text as="span" ml={1} fontStyle="italic">
                  · edited
                </Text>
              )}
            </Text>
          </Box>
        </HStack>

        {/* Action buttons */}
        {(isAuthor || isAdmin) && !isEditing && (
          <HStack spacing={1}>
            {isAuthor && (
              <Button
                leftIcon={<BsPencilSquare />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={() => onEdit(comment)}
              >
                Edit
              </Button>
            )}
            <Button
              leftIcon={<BsTrash />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => onDelete(comment._id)}
            >
              Delete
            </Button>
          </HStack>
        )}
      </Flex>

      {/* Body */}
      <Box mt={2} pl="28px">
        {isEditing ? (
          <VStack align="stretch" spacing={2}>
            <Textarea
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              size="sm"
              rows={3}
              borderRadius="md"
              autoFocus
            />
            <HStack justify="flex-end" spacing={2}>
              <Button size="xs" variant="ghost" onClick={onCancelEdit}>
                Cancel
              </Button>
              <Button
                size="xs"
                colorScheme="blue"
                onClick={() => onSaveEdit(comment._id)}
                isDisabled={!editingText.trim()}
              >
                Save
              </Button>
            </HStack>
          </VStack>
        ) : (
          <>
            <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="tall">
              <RenderContent content={displayContent} />
            </Text>
            {isLong && (
              <Button
                mt={1}
                size="xs"
                variant="link"
                colorScheme="blue"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "Show less" : "Show more"}
              </Button>
            )}
          </>
        )}

        {/* Mention badges */}
        {!isEditing && comment.mentions?.length > 0 && (
          <Wrap mt={2} spacing={1}>
            {comment.mentions.map((m) => (
              <WrapItem key={m.employeeId || m._id}>
                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                  @{m.name}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        )}

        {!isEditing && comment.attachments?.length > 0 && (
          <VStack align="stretch" spacing={2} mt={3}>
            {comment.attachments.map((attachment, index) => (
              <HStack
                key={`${attachment.url}-${index}`}
                spacing={2}
                fontSize="sm"
                bg={useColorModeValue("gray.50", "gray.700")}
                borderRadius="md"
                px={3}
                py={2}
              >
                <Icon as={BsPaperclip} color="blue.400" boxSize={3} />
                <Link
                  href={attachment.url}
                  isExternal
                  color="blue.500"
                  fontWeight="medium"
                  wordBreak="break-all"
                >
                  {attachment.filename || "Attachment"}
                </Link>
                {attachment.size ? (
                  <Text fontSize="xs" color={metaColor}>
                    ({Math.max(1, Math.round(attachment.size / 1024))} KB)
                  </Text>
                ) : null}
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
}

/* ── Main CommentSection component ───────────────────────────────────────── */
export default function CommentSection({ requestId, request }) {
  const { user } = useAuth();
  const toast = useToast();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("PUBLIC");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [sortMode, setSortMode] = useState("newest");
  const [composerExpanded, setComposerExpanded] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const commentRefs = useRef({});
  const highlightTimerRef = useRef(null);
  const attachmentInputRef = useRef(null);

  const canSeeInternal = INTERNAL_ROLES.includes(user?.role);
  const suggestions = buildSuggestions(request);

  const bgCard = useColorModeValue("white", "gray.800");
  const suggestBg = useColorModeValue("gray.50", "gray.700");
  const mentionListBg = useColorModeValue("white", "gray.700");
  const subText = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const composerBg = useColorModeValue("white", "gray.800");
  const mentionActiveBg = useColorModeValue("blue.50", "blue.900");
  const draftKey = `procurement-comment-draft-${requestId}`;

  const getEmployeeMentionLabel = (employee) => {
    if (!employee) return "";
    const fullName = [employee.firstName, employee.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return fullName || employee.name || employee.email || employee.employeeId;
  };

  const detectMentionQuery = (value, caretPosition) => {
    const uptoCaret = value.slice(0, caretPosition);
    const atIndex = uptoCaret.lastIndexOf("@");

    if (atIndex < 0) {
      setMentionQuery("");
      setMentionStart(-1);
      setActiveMentionIndex(0);
      return;
    }

    // Mention is valid only at text start or after whitespace.
    if (atIndex > 0 && !/\s/.test(uptoCaret[atIndex - 1])) {
      setMentionQuery("");
      setMentionStart(-1);
      setActiveMentionIndex(0);
      return;
    }

    const query = uptoCaret.slice(atIndex + 1);
    if (/\s/.test(query)) {
      setMentionQuery("");
      setMentionStart(-1);
      setActiveMentionIndex(0);
      return;
    }

    setMentionQuery(query);
    setMentionStart(atIndex);
    setActiveMentionIndex(0);
  };

  const handleCommentInputChange = (value) => {
    setText(value);

    // Mention picker depends on textarea caret APIs.
    // Keep manual @mentions and reset picker state for WYSIWYG mode.
    setMentionQuery("");
    setMentionStart(-1);
    setActiveMentionIndex(0);
  };

  const handleSelectMention = (employee) => {
    const label = getEmployeeMentionLabel(employee);
    if (!label || mentionStart < 0) return;

    const nextComment =
      text.slice(0, mentionStart) +
      `@${label} ` +
      text.slice(mentionStart + mentionQuery.length + 1);

    setText(nextComment);
    setMentionQuery("");
    setMentionStart(-1);
    setActiveMentionIndex(0);

    setSelectedMentions((prev) => {
      if (prev.some((mention) => mention.id === employee._id)) {
        return prev;
      }

      return [
        ...prev,
        {
          id: employee._id,
          label,
        },
      ];
    });
  };

  const handleRemoveMention = (mentionId) => {
    setSelectedMentions((prev) =>
      prev.filter((mention) => mention.id !== mentionId),
    );
  };

  const filteredMentionEmployees =
    mentionStart >= 0
      ? allEmployees
          .filter((employee) => {
            const label = getEmployeeMentionLabel(employee).toLowerCase();
            return label.includes(mentionQuery.toLowerCase());
          })
          .slice(0, 6)
      : [];

  useEffect(() => {
    if (
      mentionStart >= 0 &&
      activeMentionIndex >= filteredMentionEmployees.length
    ) {
      setActiveMentionIndex(0);
    }
  }, [mentionStart, activeMentionIndex, filteredMentionEmployees.length]);

  /* Fetch */
  const fetchComments = useCallback(async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      const res = await procurementApi.listComments(requestId);
      setComments(res.data || []);
    } catch {
      /* silently ignore */
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Restore draft when request changes
  useEffect(() => {
    if (!requestId) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setText(parsed.text || "");
      setVisibility(parsed.visibility || "PUBLIC");
      if (getPlainTextFromHtml(parsed.text || "")) {
        setComposerExpanded(true);
      }
    } catch {
      // ignore bad local draft
    }
  }, [requestId, draftKey]);

  // Persist draft for better UX
  useEffect(() => {
    if (!requestId) return;
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ text, visibility, updatedAt: Date.now() }),
      );
    } catch {
      // ignore storage errors
    }
  }, [requestId, draftKey, text, visibility]);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const data = await employeeService.getAllEmployees();
        setAllEmployees(Array.isArray(data) ? data : []);
      } catch {
        setAllEmployees([]);
      }
    };

    loadEmployees();
  }, []);

  /* Submit new comment */
  const handleSubmit = async () => {
    const plainContent = getPlainTextFromHtml(text);
    if (!plainContent && selectedFiles.length === 0) return;
    if (plainContent.length > 5000) {
      toast({
        title: "Comment is too long",
        description: "Please keep comments within 5000 characters.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      setSubmitting(true);
      let attachments = [];

      if (selectedFiles.length > 0) {
        const uploadResponse = await procurementApi.uploadCommentAttachments(
          requestId,
          selectedFiles,
        );
        attachments = uploadResponse?.data || [];
      }

      const createdResponse = await procurementApi.addComment(requestId, {
        content: plainContent || "Attachment added",
        visibility,
        attachments,
        requestUrl: window.location.href,
      });

      const createdCommentId =
        createdResponse?.data?._id || createdResponse?._id || null;
      setText("");
      setVisibility("PUBLIC");
      setMentionQuery("");
      setMentionStart(-1);
      setSelectedMentions([]);
      setActiveMentionIndex(0);
      setSelectedFiles([]);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // ignore storage errors
      }
      await fetchComments();

      if (createdCommentId) {
        setHighlightedCommentId(createdCommentId);
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current);
        }
        highlightTimerRef.current = setTimeout(() => {
          setHighlightedCommentId(null);
        }, 1000);
      }
    } catch (err) {
      toast({
        title: "Failed to post comment",
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!highlightedCommentId) return;

    const el = commentRefs.current[highlightedCommentId];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedCommentId, comments]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  /* Ctrl+Enter to submit */
  const handleKeyDown = (e) => {
    if (mentionStart >= 0 && filteredMentionEmployees.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveMentionIndex(
          (prev) => (prev + 1) % filteredMentionEmployees.length,
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveMentionIndex(
          (prev) =>
            (prev - 1 + filteredMentionEmployees.length) %
            filteredMentionEmployees.length,
        );
        return;
      }
      if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleSelectMention(filteredMentionEmployees[activeMentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionQuery("");
        setMentionStart(-1);
        return;
      }
    }

    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleSubmit();
  };

  /* Edit */
  const handleEdit = (comment) => {
    setEditingId(comment._id);
    setEditingText(comment.content);
  };

  const handleSaveEdit = async (commentId) => {
    if (!editingText.trim()) return;
    try {
      await procurementApi.editComment(requestId, commentId, {
        content: editingText.trim(),
      });
      setEditingId(null);
      setEditingText("");
      await fetchComments();
    } catch (err) {
      toast({
        title: "Failed to edit comment",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  /* Delete */
  const handleDelete = async (commentId) => {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await procurementApi.deleteComment(requestId, commentId);
      await fetchComments();
    } catch {
      toast({
        title: "Failed to delete comment",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /* Use a suggestion as comment text */
  const applySuggestion = (s) => {
    setText((prev) => {
      const current = getPlainTextFromHtml(prev);
      return current ? `${current} ${s}` : s;
    });
    setShowSuggestions(false);
    setComposerExpanded(true);
  };

  const composerTextLength = getPlainTextFromHtml(text).length;

  const handleAttachmentSelect = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    if (nextFiles.length === 0) return;

    setSelectedFiles((prev) => [...prev, ...nextFiles].slice(0, 5));
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  };

  const handleRemoveSelectedFile = (fileIndex) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const handleCancelCompose = () => {
    setText("");
    setMentionQuery("");
    setMentionStart(-1);
    setSelectedMentions([]);
    setActiveMentionIndex(0);
    setSelectedFiles([]);
    setVisibility("PUBLIC");
    setComposerExpanded(false);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
    try {
      localStorage.removeItem(draftKey);
    } catch {
      // ignore storage errors
    }
  };

  const publicCount = comments.filter((c) => c.visibility === "PUBLIC").length;
  const internalCount = comments.filter(
    (c) => c.visibility === "INTERNAL",
  ).length;

  const visibleComments = useMemo(() => {
    let list = [...comments];

    if (filterMode === "public") {
      list = list.filter((c) => c.visibility === "PUBLIC");
    } else if (filterMode === "internal") {
      list = list.filter((c) => c.visibility === "INTERNAL");
    } else if (filterMode === "mine") {
      list = list.filter(
        (c) => String(c.authorId?._id || c.authorId) === String(user?._id),
      );
    } else if (filterMode === "mentions") {
      list = list.filter((c) =>
        Array.isArray(c.mentions)
          ? c.mentions.some(
              (m) => String(m.employeeId || m._id) === String(user?._id),
            )
          : false,
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (c) =>
          (c.content || "").toLowerCase().includes(q) ||
          (c.authorName || "").toLowerCase().includes(q),
      );
    }

    list.sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortMode === "oldest" ? aTime - bTime : bTime - aTime;
    });

    return list;
  }, [comments, filterMode, searchQuery, sortMode, user?._id]);

  const groupedComments = useMemo(() => {
    const groups = {
      Today: [],
      Yesterday: [],
      Earlier: [],
    };

    for (const comment of visibleComments) {
      const label = getDateGroupLabel(comment.createdAt);
      groups[label].push(comment);
    }

    return groups;
  }, [visibleComments]);

  return (
    <Box bg={bgCard} borderRadius="xl" p={6} shadow="sm">
      {/* Section heading */}
      <HStack mb={5} spacing={3}>
        <Icon as={BsChatDots} boxSize={5} color="blue.500" />
        <Heading size="md">Comments</Heading>
        <Badge colorScheme="blue" borderRadius="full" px={2}>
          {publicCount} public
        </Badge>
        {canSeeInternal && internalCount > 0 && (
          <Badge colorScheme="yellow" borderRadius="full" px={2}>
            {internalCount} internal
          </Badge>
        )}
      </HStack>

      <Box
        mb={4}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="lg"
        p={3}
      >
        <VStack align="stretch" spacing={3}>
          <Input
            placeholder="Search by author or comment text"
            size="sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Flex
            justify="space-between"
            align={{ base: "stretch", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={2}
          >
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="xs"
                variant={filterMode === "all" ? "solid" : "outline"}
                onClick={() => setFilterMode("all")}
              >
                All
              </Button>
              <Button
                size="xs"
                variant={filterMode === "public" ? "solid" : "outline"}
                onClick={() => setFilterMode("public")}
              >
                Public
              </Button>
              {canSeeInternal && (
                <Button
                  size="xs"
                  variant={filterMode === "internal" ? "solid" : "outline"}
                  onClick={() => setFilterMode("internal")}
                >
                  Internal
                </Button>
              )}
              <Button
                size="xs"
                variant={filterMode === "mine" ? "solid" : "outline"}
                onClick={() => setFilterMode("mine")}
              >
                My comments
              </Button>
              <Button
                size="xs"
                variant={filterMode === "mentions" ? "solid" : "outline"}
                onClick={() => setFilterMode("mentions")}
              >
                Mentioning me
              </Button>
            </HStack>
            <HStack spacing={2}>
              <Button
                size="xs"
                variant={sortMode === "newest" ? "solid" : "outline"}
                onClick={() => setSortMode("newest")}
              >
                Newest
              </Button>
              <Button
                size="xs"
                variant={sortMode === "oldest" ? "solid" : "outline"}
                onClick={() => setSortMode("oldest")}
              >
                Oldest
              </Button>
              <Badge colorScheme="gray">{visibleComments.length} shown</Badge>
            </HStack>
          </Flex>
        </VStack>
      </Box>

      {/* ── Compose box (Jira-style compact editor) ── */}
      <Box mb={6}>
        {!composerExpanded ? (
          <Box
            bg={composerBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            px={3}
            py={2}
            cursor="text"
            onClick={() => setComposerExpanded(true)}
          >
            <HStack spacing={2}>
              <Avatar size="xs" name={user?.name || user?.email || "You"} />
              <Text color={subText} fontSize="sm">
                Add a comment...
              </Text>
            </HStack>
          </Box>
        ) : (
          <Box
            bg={composerBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            p={3}
          >
            <Box mb={2} onKeyDown={handleKeyDown}>
              <WysiwygEditor
                value={text}
                onChange={handleCommentInputChange}
                placeholder="Add a comment... Use @name to mention someone"
              />
            </Box>
            <Flex justify="space-between" mb={2}>
              <Text fontSize="xs" color={subText}>
                Use @ to mention teammates.
              </Text>
              <Text
                fontSize="xs"
                color={composerTextLength > 4800 ? "orange.400" : subText}
              >
                {composerTextLength}/5000
              </Text>
            </Flex>

            <Box mb={3}>
              <input
                ref={attachmentInputRef}
                type="file"
                multiple
                hidden
                onChange={handleAttachmentSelect}
              />
              <Button
                size="sm"
                variant="outline"
                leftIcon={<BsPaperclip />}
                onClick={() => attachmentInputRef.current?.click()}
                isDisabled={submitting || selectedFiles.length >= 5}
              >
                Attach files
              </Button>
              <Text mt={1} fontSize="xs" color={subText}>
                Up to 5 files, 10 MB each.
              </Text>

              {selectedFiles.length > 0 && (
                <VStack align="stretch" spacing={2} mt={3}>
                  {selectedFiles.map((file, index) => (
                    <HStack
                      key={`${file.name}-${index}`}
                      justify="space-between"
                      bg={mentionListBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      px={3}
                      py={2}
                    >
                      <HStack spacing={2} minW={0}>
                        <Icon as={BsPaperclip} color="blue.400" boxSize={3} />
                        <Text fontSize="sm" noOfLines={1}>
                          {file.name}
                        </Text>
                        <Text fontSize="xs" color={subText} whiteSpace="nowrap">
                          ({Math.max(1, Math.round(file.size / 1024))} KB)
                        </Text>
                      </HStack>
                      <Button
                        size="xs"
                        variant="ghost"
                        minW="auto"
                        onClick={() => handleRemoveSelectedFile(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <Icon as={BsX} />
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>

            {mentionStart >= 0 && (
              <Box
                mb={2}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                bg={mentionListBg}
                maxH="180px"
                overflowY="auto"
              >
                {filteredMentionEmployees.length === 0 ? (
                  <Text px={3} py={2} fontSize="xs" color={subText}>
                    No users found for "@{mentionQuery}"
                  </Text>
                ) : (
                  filteredMentionEmployees.map((employee) => {
                    const label = getEmployeeMentionLabel(employee);
                    return (
                      <Button
                        key={employee._id}
                        variant="ghost"
                        justifyContent="flex-start"
                        w="100%"
                        borderRadius={0}
                        size="sm"
                        onClick={() => handleSelectMention(employee)}
                        bg={
                          filteredMentionEmployees[activeMentionIndex]?._id ===
                          employee._id
                            ? mentionActiveBg
                            : "transparent"
                        }
                      >
                        @{label}
                      </Button>
                    );
                  })
                )}
              </Box>
            )}

            {selectedMentions.length > 0 && (
              <HStack spacing={2} mb={2} flexWrap="wrap">
                {selectedMentions.map((mention) => (
                  <Badge
                    key={mention.id}
                    colorScheme="blue"
                    borderRadius="full"
                    px={2}
                    py={1}
                  >
                    @{mention.label}
                    <Button
                      ml={1}
                      size="xs"
                      variant="ghost"
                      minW="auto"
                      h="16px"
                      p={0}
                      onClick={() => handleRemoveMention(mention.id)}
                      aria-label={`Remove mention ${mention.label}`}
                    >
                      x
                    </Button>
                  </Badge>
                ))}
              </HStack>
            )}

            {/* Smart suggestions toggle */}
            {suggestions.length > 0 && (
              <Box mb={3}>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  rightIcon={
                    showSuggestions ? <BsChevronUp /> : <BsChevronDown />
                  }
                  onClick={() => setShowSuggestions((v) => !v)}
                >
                  Smart suggestions
                </Button>
                <Collapse in={showSuggestions} animateOpacity>
                  <Wrap
                    mt={2}
                    spacing={2}
                    bg={suggestBg}
                    p={3}
                    borderRadius="md"
                  >
                    {suggestions.map((s, i) => (
                      <WrapItem key={i}>
                        <Badge
                          colorScheme="purple"
                          variant="subtle"
                          cursor="pointer"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          _hover={{ bg: "purple.100" }}
                          onClick={() => applySuggestion(s)}
                        >
                          {s}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Collapse>
              </Box>
            )}

            <Flex justify="space-between" align="center">
              {canSeeInternal ? (
                <Select
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                  size="sm"
                  w="180px"
                  borderRadius="md"
                >
                  <option value="PUBLIC">🌐 Public</option>
                  <option value="INTERNAL">🔒 Internal (Staff only)</option>
                </Select>
              ) : (
                <HStack spacing={1}>
                  <Icon as={BsChatDots} color="blue.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.500">
                    Public comment
                  </Text>
                </HStack>
              )}

              <HStack>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelCompose}
                  isDisabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  leftIcon={<BsSend />}
                  colorScheme="blue"
                  size="sm"
                  onClick={handleSubmit}
                  isLoading={submitting}
                  isDisabled={
                    (composerTextLength === 0 && selectedFiles.length === 0) ||
                    composerTextLength > 5000
                  }
                  borderRadius="md"
                >
                  Comment
                </Button>
              </HStack>
            </Flex>
          </Box>
        )}
      </Box>

      <Divider mb={5} />

      {/* ── Comment list ── */}
      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner color="blue.400" />
        </Flex>
      ) : visibleComments.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={10}
          gap={2}
          color="gray.400"
        >
          <Icon as={BsChatDots} boxSize={8} />
          <Text fontSize="sm">
            {comments.length === 0
              ? "No comments yet. Be the first to comment."
              : "No comments match your current filters."}
          </Text>
        </Flex>
      ) : (
        <VStack align="stretch" spacing={5}>
          {["Today", "Yesterday", "Earlier"].map((group) => {
            const items = groupedComments[group];
            if (!items?.length) return null;

            return (
              <Box key={group}>
                <HStack mb={2} spacing={2}>
                  <Text fontSize="xs" fontWeight="bold" color={subText}>
                    {group}
                  </Text>
                  <Divider />
                </HStack>
                <VStack align="stretch" spacing={3}>
                  {items.map((comment) => (
                    <Box
                      key={comment._id}
                      ref={(node) => {
                        if (node) {
                          commentRefs.current[comment._id] = node;
                        } else {
                          delete commentRefs.current[comment._id];
                        }
                      }}
                    >
                      <CommentCard
                        comment={comment}
                        currentUser={user}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        editingId={editingId}
                        editingText={editingText}
                        setEditingText={setEditingText}
                        onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit}
                        isHighlighted={highlightedCommentId === comment._id}
                      />
                    </Box>
                  ))}
                </VStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
